/**
 * Alpha Checkout — /alpha-trial/:accessKey
 *
 * Invite-only Stripe Sandbox checkout for approved alpha testers. The URL
 * carries a single-use signed token; the page prefills the lead's waitlist
 * data and opens the hosted Stripe checkout.
 *
 * Industry design system (auth grammar): light ground, hairline top nav
 * with the bordered "Z" plate + wordmark, a 2-column grid — left hero
 * column with accent kicker, hairline info cards with ink icon plates and
 * a blueprint note; right blueprint form card with uppercase mini-labels
 * over square hairline inputs and the solid accent primary CTA. Lucide
 * icons at stroke 1.5; no gradients, no glass, no pills.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { ArrowLeft, CheckCircle2, Shield } from 'lucide-react';
import { billingService } from '../services/billing';
import apiClient from '../services/api';

const initialForm = {
  full_name: '',
  company_name: '',
  aircraft_prefix: '',
  phone_number: '',
  email: '',
};

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';

const labelSx = {
  display: 'block',
  fontFamily: HEADING,
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: MUTED58,
  mb: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: 38,
  fontSize: 14,
  background: 'transparent',
  border: '1px solid var(--color-divider)',
  borderRadius: 0,
  padding: '0 11px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
};

const FIELDS = [
  { key: 'full_name', id: 'alpha-full-name', label: 'Nome completo', placeholder: 'Ex.: Flavio Almeida', span: '1 / -1' },
  { key: 'company_name', id: 'alpha-company', label: 'Empresa', placeholder: 'Nome da operacao' },
  { key: 'aircraft_prefix', id: 'alpha-prefix', label: 'Prefixo da aeronave', placeholder: 'PR-ABC', upper: true },
  { key: 'phone_number', id: 'alpha-phone', label: 'WhatsApp', placeholder: '+55 11 99999-9999' },
  { key: 'email', id: 'alpha-email', label: 'E-mail', placeholder: 'operacao@empresa.com', type: 'email' },
];

export default function AlphaCheckout() {
  const { accessKey = '' } = useParams();
  const checkoutToken = decodeURIComponent(accessKey || '');
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Write-only breadcrumb (kept for parity with the previous page): records
  // where the prefill came from without rendering it.
  const [, setPrefillSource] = useState('');

  // Prefill the form from whatever the lead already submitted on the public
  // waitlist signup. The admin "Generate Link" flow ties the invite token to
  // the lead row, so we can look up name/email/phone/aircraft from the
  // public endpoint without exposing the raw token back.
  useEffect(() => {
    if (!checkoutToken) return;
    let cancelled = false;
    apiClient
      .get('/landing/alpha-trial/lookup/', {
        params: { token: checkoutToken },
        __skipAuthRefresh: true,
        __skipAuthHeader: true,
      })
      .then((response) => {
        if (cancelled) return;
        const lead = response?.data?.lead;
        if (!lead) return;
        setForm((previous) => ({
          ...previous,
          full_name: previous.full_name || lead.full_name || '',
          company_name: previous.company_name || lead.company_name || '',
          aircraft_prefix:
            previous.aircraft_prefix ||
            String(lead.aircraft_prefix || '').toUpperCase(),
          phone_number: previous.phone_number || lead.phone_number || '',
          email: previous.email || lead.email || '',
        }));
        setPrefillSource(String(response?.data?.source || '') || 'waitlist_trial');
      })
      .catch(() => {
        // Silent: a 404 here means the invite token isn't waitlist-bound or
        // has already been consumed. The page should still let the user
        // type their data manually, so we don't surface an error banner.
      });
    return () => {
      cancelled = true;
    };
  }, [checkoutToken]);

  const canSubmit = useMemo(() => {
    return (
      form.full_name.trim() &&
      form.company_name.trim() &&
      form.aircraft_prefix.trim() &&
      form.phone_number.trim() &&
      form.email.trim()
    );
  }, [form]);

  const handleFieldChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: field === 'aircraft_prefix' ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || busy) return;

    setBusy(true);
    setError('');
    try {
      const data = await billingService.createCheckoutSession({
        plan_tier: 'pro',
        checkout_access_token: checkoutToken,
        full_name: form.full_name.trim(),
        company_name: form.company_name.trim(),
        aircraft_prefix: form.aircraft_prefix.trim().toUpperCase(),
        phone_number: form.phone_number.trim(),
        billing_email: form.email.trim(),
        skip_auth_header: true,
      });

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      setError(
        [data?.error, data?.detail].filter(Boolean).join(': ') ||
          'Nao foi possivel abrir o checkout agora.'
      );
    } catch (requestError) {
      setError(
        [
          requestError?.response?.data?.error,
          requestError?.response?.data?.detail,
        ]
          .filter(Boolean)
          .join(': ') || 'Acesso invalido ou checkout indisponivel.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Top hairline nav: back link + Z plate wordmark ── */}
      <Box
        component="nav"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 4,
          py: '14px',
          borderBottom: '1px solid var(--color-divider)',
          flexWrap: 'wrap',
        }}
      >
        <Box
          component={RouterLink}
          to="/whitepaper"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            minHeight: 32,
            px: '12px',
            border: '1px solid var(--color-divider)',
            color: 'var(--color-text)',
            fontFamily: HEADING,
            fontSize: '10.5px',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            textDecoration: 'none',
            '&:hover': { borderColor: 'rgba(29,31,32,0.45)' },
          }}
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Voltar para a landing
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 28, height: 28, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 14, fontWeight: 600 }}>
            Z
          </Box>
          <Box component="span" sx={{ fontFamily: HEADING, fontSize: 18, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Zulbot
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: 4,
          py: '36px',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.05fr) minmax(0, 0.95fr)' },
          gap: '28px',
          alignItems: 'start',
          boxSizing: 'border-box',
        }}
      >
        {/* ── Left: hero + info cards + sharing note ── */}
        <Box sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ fontFamily: HEADING, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: ACC7 }}>
            Alpha privado com Stripe Sandbox
          </Box>
          <Typography
            component="h1"
            sx={{ m: '10px 0 0', fontFamily: HEADING, fontSize: { xs: 34, md: 42 }, fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.02em', maxWidth: '22ch' }}
          >
            Assinatura liberada apenas para convidados do alpha.
          </Typography>
          <Typography sx={{ m: '14px 0 0', fontSize: 14.5, lineHeight: 1.6, maxWidth: '58ch', color: MUTED72 }}>
            Este link contem um convite unico (token) gerado pela equipe Zulbot. Ele autoriza o checkout de teste sem
            expor o trial publico na landing.
          </Typography>

          <Box
            sx={{
              mt: '22px',
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: '12px',
            }}
          >
            {[
              {
                icon: <Shield size={15} strokeWidth={1.5} />,
                title: 'Acesso restrito',
                body: 'Somente quem receber este link com o token de convite consegue abrir o checkout.',
              },
              {
                icon: <CheckCircle2 size={15} strokeWidth={1.5} />,
                title: 'Cartao de teste',
                body: 'Use `4242 4242 4242 4242` no Stripe Sandbox para validar o fluxo.',
              },
            ].map((card) => (
              <Box key={card.title} sx={{ p: '14px 15px', border: '1px solid var(--color-divider)', background: 'transparent', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box sx={{ flexShrink: 0, display: 'grid', placeItems: 'center', width: 26, height: 24, background: INK, color: 'var(--color-bg)' }}>
                    {card.icon}
                  </Box>
                  <Box component="span" sx={{ fontFamily: HEADING, fontSize: 13, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                    {card.title}
                  </Box>
                </Box>
                <Typography sx={{ mt: '8px', fontSize: 12.5, lineHeight: 1.5, color: MUTED72 }}>
                  {card.body}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box className="blueprint" sx={{ mt: '16px', p: '16px 17px', background: 'transparent', boxShadow: 'var(--ind-shadow-sm)' }}>
            <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />
            <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTED58 }}>
              Quando compartilhar
            </Box>
            <Typography sx={{ mt: '8px', fontSize: 12.5, lineHeight: 1.6, color: MUTED72 }}>
              Envie este link somente para o alpha tester aprovado. Cada convite usa um token unico; apos um checkout
              concluido com sucesso, o token e consumido e um novo convite deve ser emitido se necessario.
            </Typography>
          </Box>
        </Box>

        {/* ── Right: blueprint form card ── */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          className="blueprint"
          sx={{ p: '24px', background: 'transparent', boxShadow: 'var(--ind-shadow-md)', minWidth: 0 }}
        >
          <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

          <Box component="span" sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: ACC7 }}>
            Checkout privado
          </Box>
          <Typography
            component="h2"
            sx={{ m: '8px 0 0', fontFamily: HEADING, fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase' }}
          >
            Dados do operador
          </Typography>
          <Typography sx={{ m: '7px 0 0', fontSize: 13, lineHeight: 1.55, color: MUTED72 }}>
            Preencha os dados abaixo para abrir o checkout seguro e provisionar o ambiente de teste.
          </Typography>

          <Box
            sx={{
              mt: '18px',
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: '14px 18px',
            }}
          >
            {FIELDS.map((field) => (
              <Box key={field.key} sx={{ gridColumn: { xs: 'auto', md: field.span || 'auto' }, minWidth: 0 }}>
                <Box component="label" htmlFor={field.id} sx={labelSx}>
                  {field.label}
                </Box>
                <input
                  id={field.id}
                  className="ind-input"
                  type={field.type || 'text'}
                  placeholder={field.placeholder}
                  value={form[field.key]}
                  onChange={(event) => handleFieldChange(field.key, event.target.value)}
                  disabled={busy}
                  style={{
                    ...inputStyle,
                    ...(field.upper
                      ? { textTransform: 'uppercase', fontFamily: HEADING, fontWeight: 600, letterSpacing: '0.06em' }
                      : {}),
                  }}
                />
              </Box>
            ))}
          </Box>

          {error ? (
            <Box sx={{ mt: '14px', p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK }}>
              {error}
            </Box>
          ) : null}

          <Box
            sx={{
              mt: '16px',
              p: '12px 13px',
              border: `1px solid ${ACC}`,
              background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)',
              fontSize: 12.5,
              lineHeight: 1.55,
              color: ACC8,
            }}
          >
            <Box sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', mb: '6px' }}>
              O que acontece em seguida
            </Box>
            <Box component="ol" sx={{ m: 0, pl: '18px', color: 'var(--color-text)', '& li': { mb: '3px' } }}>
              <li>Você abre o checkout no Stripe Sandbox e confirma o pagamento.</li>
              <li>
                Após a confirmação, você será levado para criar o usuário e a senha do administrador.
                Também enviamos o link de ativação por e-mail como fallback.
              </li>
              <li>Pelo link, você cria a senha de administrador e acessa o painel da Zulbot.</li>
            </Box>
          </Box>

          <Box
            component="button"
            type="submit"
            disabled={!canSubmit || busy}
            sx={{
              width: '100%',
              minHeight: 44,
              mt: '20px',
              border: `1px solid ${ACC}`,
              background: ACC,
              color: 'var(--color-bg)',
              fontFamily: HEADING,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              borderRadius: 0,
              cursor: 'pointer',
              '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
              '&:active': { background: ACC7 },
              '&:disabled': { opacity: 0.45, cursor: 'default' },
            }}
          >
            {busy ? 'Abrindo checkout...' : 'Ir para o Stripe Sandbox'}
          </Box>

          <Typography sx={{ mt: '12px', textAlign: 'center', fontSize: 11.5, lineHeight: 1.6, color: MUTED58 }}>
            Fluxo exclusivo para alpha testers. Para o publico geral, a landing continua apenas com a wait list.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
